import cn from 'classnames';
import { Modal, Button, ModalDrawerControllerProps, ModalCloseReason, Group } from '@economic/gaudi';
import React from 'react';
import { BulkBookAndSendViewData, BulkBookAndSendFormData, InvoicingMessageType } from '../types';
import { t } from '../../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../../shared/components/DisplayMessages';
import { Field } from '../../../../../../shared/components/Form';
import { BookOnlyConfirmationDialogContent } from '../BookOnlyConfirmationDialogContent';
import { useField } from 'react-final-form';
import { SendAsEInvoice, SendAsEmail } from '../../../shared/components/SendBy';
import { EmailOptionsDrawerToggle } from '../../../shared/components/EmailOptionsDrawerToggle';
import { RouteDisclosure } from '../../../../../../shared/components/RouteDisclosure';
import { InvoicingMessage } from '../../../../../../../types/internal-api';
import { FormApi } from 'final-form';
import { SendEmailOptionsDrawer, FullAttachmentOptions } from '../../../shared/components/SendEmailOptionsDrawer';
import { SalesModalForm } from '../../../shared/components/SalesModalForm';

export interface BulkBookDraftInvoicesDialogProps {
    loading: boolean;
    onHide: (reason: ModalCloseReason) => void;
    onSubmit: (formData: BulkBookAndSendFormData) => void;
    viewData?: BulkBookAndSendViewData;
    formData?: BulkBookAndSendFormData;
    error: boolean;
    disclosure?: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
}

export function BulkBookDraftInvoicesDialog({
    loading,
    onHide,
    onSubmit,
    viewData,
    formData,
    error,
    disclosure,
}: BulkBookDraftInvoicesDialogProps) {
    disclosure = disclosure || <RouteDisclosure routeTrigger={'#book-bulk'} />;

    function onFormAction(form: FormApi, bookOnly: boolean) {
        form.change('bookOnly', bookOnly);
        form.submit();
    }

    let errorMessages: InvoicingMessage[] = [];
    if (!!error) {
        errorMessages = [{ type: InvoicingMessageType.Error, message: t`FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR` }];
    } else if (viewData && viewData.sendByEmailCount === 0 && viewData.sendElectronicallyCount === 0) {
        errorMessages = viewData.blockingErrors;
    }

    return (
        <SalesModalForm
            loading={loading}
            loadingSpinnerWithText
            errorTitle={t`FORM_BOOKANDSEND_ERRORS_HEADER_CANNOTBOOK`}
            errorMessages={errorMessages}
            ariaLabel={t`FORM_BULKBOOKANDSEND_TITLE`}
            disclosure={disclosure}
            className={cn('book-and-send-dialog', 'form')}
            onHide={onHide}
            drawer={_ => (
                <SendEmailOptionsDrawer hideRecipientsSection>
                    <FullAttachmentOptions />
                </SendEmailOptionsDrawer>
            )}
            initialData={formData}
            onSubmit={onSubmit}>
            {(form, drawer) =>
                viewData && (
                    <BulkBookDraftInvoicesDialogContent
                        viewData={viewData}
                        drawer={drawer}
                        onFormAction={(bookOnly: boolean) => onFormAction(form, bookOnly)}
                    />
                )
            }
        </SalesModalForm>
    );
}

function BulkBookDraftInvoicesDialogContent({
    viewData,
    drawer,
    onFormAction,
}: {
    viewData: BulkBookAndSendViewData;
    drawer: ModalDrawerControllerProps;
    onFormAction: (bookOnly: boolean) => void;
}) {
    const [showBookOnlyConfirmation, setBookOnlyConfirmation] = React.useState<boolean>(false);
    const {
        input: { value: isChangeInvoiceDateChecked },
    } = useField<boolean>('changeInvoiceDate');

    if (showBookOnlyConfirmation) {
        return (
            <BookOnlyConfirmationDialogContent
                onBack={() => setBookOnlyConfirmation(false)}
                onOk={() => onFormAction(true)}
                text={t`FORM_BOOKANDSEND_BOOK_ONLY_CONFIRMATION_TEXT`}
            />
        );
    }

    function onBookOnlyAction() {
        if (viewData.skipBookOnlyConfirmation) {
            onFormAction(true);
        } else {
            if (drawer.visible) {
                drawer.hide();
            }

            setBookOnlyConfirmation(true);
        }
    }

    function showDrawer() {
        if (viewData.sendByEmailCount > 0) {
            drawer.show();
        }
    }

    return (
        <>
            <Modal.Header>{t`FORM_BULKBOOKANDSEND_TITLE`}</Modal.Header>

            <Modal.Content>
                {viewData.sendElectronicallyCount > 0 && (
                    <SendAsEInvoice
                        title={t('FORM_BULKBOOKANDSEND_ELECTRONIC_HEADER', { COUNT: viewData.sendElectronicallyCount })}
                        text={<em>{t`FORM_BULKBOOKANDSEND_ELECTRONIC_DESCRIPTION`}</em>}
                    />
                )}

                {viewData.sendByEmailCount > 0 && (
                    <>
                        <SendAsEmail title={t(`FORM_BULKBOOKANDSEND_EMAIL_HEADER`, { COUNT: viewData.sendByEmailCount })} />
                        <EmailOptionsDrawerToggle
                            drawerVisible={drawer.visible}
                            onShowDrawer={showDrawer}
                            onToggleDrawer={drawer.toggle}
                        />
                    </>
                )}

                <div className="book-and-send-dialog__messages">
                    <DisplayMessages messages={viewData.nonBlockingWarnings} />
                </div>

                <div className="book-and-send-dialog__options">
                    <div className="fields-container">
                        <Field.Checkbox
                            name="changeInvoiceDate"
                            text={t`FORM_BULKBOOKANDSEND_OPTIONS_CHANGEINVOICEDATETO`}
                            fieldClassName="field--no-height mr-4"
                        />
                        <div className="align-left">
                            <Field.DatePicker
                                name="newInvoiceDate"
                                disabled={!isChangeInvoiceDateChecked}
                                fieldClassName="field--no-height"
                            />
                        </div>
                    </div>

                    {viewData.canIncludePaymentLink && viewData.sendByEmailCount > 0 && (
                        <Field.Checkbox
                            name="includePaymentLink"
                            text={t`FORM_BOOKANDSEND_EMAIL_INCLUDE_PAYMENT_LINK`}
                            fieldClassName="field--no-height"
                        />
                    )}

                    <Field.Checkbox
                        name="showInvoicesAfterBooking"
                        text={t`FORM_BOOKANDSEND_SHOW_INVOICE`}
                        fieldClassName="field--no-height"
                    />
                </div>
            </Modal.Content>

            <Modal.Footer>
                <Group>
                    <Button onClick={onBookOnlyAction}>{t`FORM_BOOKANDSEND_BOOK_TEXT`}</Button>
                    <Button
                        appearance="primary"
                        onClick={() => onFormAction(false)}
                        autoFocus>{t`FORM_BOOKANDSEND_SEND_TEXT`}</Button>
                </Group>
            </Modal.Footer>
        </>
    );
}
